// src/utils/taxExemptionUi.js — taxexempt3-8c4f2a1d (tax codes on every line, Stage S3)
//
// The website's side of customer tax exemption. The rules and words are shared with the server and
// the phone (src/utils/lineTax.js, generated); this file only adds what a screen needs:
//   • useTaxCodesDoc(orgId) — settings/taxCodes, live (every member can read it; only the server writes it)
//   • who may change an exemption — office roles (Owner, Admin, Manager, Office Staff = canBatchInvoice),
//     the same list the server checks (taxCodes.EDIT_ROLES). Display only: the server decides.
//   • the two server calls, and the office-only marks: qbTaxCodeHold (QuickBooks has no code for what
//     was charged) and taxCheckNeeded (tax couldn't be checked when the document was made).
// NOTHING here is ever shown to a customer.
import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase/config';
import { canBatchInvoice } from './permissions';
import { exemptionsSwitchOn } from './lineTax';

/** settings/taxCodes: undefined while loading, null when missing, else its data. */
export function useTaxCodesDoc(orgId) {
    const [stored, setStored] = useState(undefined);
    useEffect(() => {
        if (!orgId) return undefined;
        setStored(undefined);
        return onSnapshot(doc(db, `organizations/${orgId}/settings/taxCodes`),
            (snap) => setStored(snap.exists() ? snap.data() : null),
            (err) => { console.error('taxExemptionUi: could not load settings/taxCodes', err); setStored(null); });
    }, [orgId]);
    return stored;
}

/** Are exemptions on for this company? false while the doc is still loading (nothing shows). */
export const exemptionsOnFor = (orgId, taxCodesDoc) => taxCodesDoc !== undefined && exemptionsSwitchOn(orgId, taxCodesDoc);

/** May this person change a customer's exemption or switch it off on a document? */
export const canChangeTaxExemption = (user) => canBatchInvoice(user);

export const setCustomerTaxExemptionCall = (payload) => httpsCallable(functions, 'setCustomerTaxExemption')(payload);
export const setDocumentTaxExemptionCall = (payload) => httpsCallable(functions, 'setDocumentTaxExemption')(payload);

/** "Oct 30" from an ISO time; '' if it isn't one. */
export const shortDate = (iso) => {
    const d = typeof iso === 'string' ? new Date(iso) : null;
    return d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
};

/** The QuickBooks hold on an invoice (office only), or null. */
export const qbTaxCodeHoldOf = (invoice) => {
    const h = invoice && invoice.qbTaxCodeHold;
    return h && typeof h === 'object' && typeof h.message === 'string' && !invoice.qbInvoiceId ? h : null;
};

export const TAX_CHECK_TEXT = "Tax couldn't be checked when this was made. Check the tax before sending anything to the customer.";
/** The "tax couldn't be checked" mark, or null. */
export const taxCheckNeededOf = (d) => {
    const m = d && d.taxCheckNeeded;
    return m && typeof m === 'object' && !Array.isArray(m) ? m : null;
};

// taxexempt3-8c4f2a1d — after "Charge tax on this document" / "Apply exemption again" the page shows the
// document AS THE SERVER NOW HAS IT (re-read after the call), never figures worked out here. Only the fields
// that call changes are laid over the page's copy; one the server no longer has (e.g. the switch-off after
// "Apply exemption again") is removed.
export const EXEMPTION_CHANGE_FIELDS = Object.freeze(['lineItems', 'subtotal', 'taxAmount', 'total', 'taxParts', 'totalsEngineVersion',
    'taxExemptionApplied', 'taxExemptionOff', 'taxExemptionHistory', 'amountPaid', 'balanceDue']);
export const EXEMPTION_COLLECTIONS = Object.freeze({ invoice: 'invoices', quote: 'quotes', job: 'jobs' });

/** doc with the re-read document's changed fields laid over it. fresh = { id, data } or null. */
export function withExemptionChange(doc, fresh) {
    if (!doc || !fresh || !fresh.data || fresh.id !== doc.id) return doc;
    const out = { ...doc };
    EXEMPTION_CHANGE_FIELDS.forEach((k) => { if (k in fresh.data) out[k] = fresh.data[k]; else delete out[k]; });
    return out;
}

/** A server refusal's message for the person, never a raw code. */
export const callErrorText = (err) => (err && typeof err.message === 'string' && err.message && !/^internal$/i.test(err.message)
    ? err.message.replace(/^FirebaseError:\s*/, '') : 'Could not save. Please try again.');
